import { useCallback, useEffect } from 'react';
import { useRegularNodesFactory } from './factories/useRegularNodesFactory';
import { useGroupNodesFactory } from './factories/useGroupNodesFactory';
import { useSweepCanvasPropsCtx } from './internal-context/SweepCanvasPropsCtx';
import { useSweepCanvasState } from './internal-context/CanvasStateContext';
import { useEdgesFactory } from './factories/useEdgesFactory';
import { CanvasElementType } from './canvas-types';
import useStandaloneNodesFactory from './factories/useStandaloneNodesFactory';
import { useSectionNodesFactory } from './factories/useSectionNodesFactory';
import useStickyNotesNodesFactory from './factories/useStickyNotesNodesFactory';
import useTextNodesFactory from './factories/useTextNodesFactory';

export const useLoadNodesAndEdges = () => {
  const {
    sweepNodes,
    sweepEdges,
    sweepGroups,
    canvasMode,
    sweepStandaloneNodes,
    sweepSections,
    sweepStickyNotesNodes,
    sweepTextNodes,
  } = useSweepCanvasPropsCtx();
  const { stateInitialized } = useSweepCanvasState();

  const {
    canvasNodes: _nodes,
    canvasEdges: _edges,
    setCanvasNodes,
    setCanvasEdges,
  } = useSweepCanvasState();

  const { createFlowNodesFromSweepNodes } = useRegularNodesFactory();
  const { calculateGroupNodes } = useGroupNodesFactory();
  const { calculateEdges } = useEdgesFactory();
  const { createFlowNodesFromSweepStandaloneNodes } = useStandaloneNodesFactory();
  const { createFlowNodesFromSweepSections } = useSectionNodesFactory();
  const { createFlowNodesFromSweepStickyNotesNodes } = useStickyNotesNodesFactory();
  const { createFlowNodesFromSweepTextNodes } = useTextNodesFactory();

  const calculateNodes = useCallback(() => {
    const { groupNodes, groupLabelNodes, groupOverlayNodes } = calculateGroupNodes({
      sweepNodes,
      sweepGroups,
    });

    const standaloneNodes = createFlowNodesFromSweepStandaloneNodes({
      standaloneNodes: sweepStandaloneNodes,
    });

    const sectionNodes = createFlowNodesFromSweepSections({
      sections: sweepSections,
    });

    const stickyNotesNodes = createFlowNodesFromSweepStickyNotesNodes({
      stickyNotesNodes: sweepStickyNotesNodes,
    });

    const textNodes = createFlowNodesFromSweepTextNodes({
      canvasTextNodes: sweepTextNodes,
    });

    const regularNodes = createFlowNodesFromSweepNodes({
      sweepNodes,
      sweepEdges,
      sweepGroupIds: sweepGroups.map(({ id }) => id),
      canvasMode,
    });
    return {
      allNodes: [
        ...groupNodes,
        ...regularNodes,
        ...groupLabelNodes,
        ...standaloneNodes,
        ...sectionNodes,
        ...stickyNotesNodes,
        ...textNodes,
        ...groupOverlayNodes,
      ],
      regularNodes,
    };
  }, [
    calculateGroupNodes,
    createFlowNodesFromSweepStandaloneNodes,
    sweepStandaloneNodes,
    createFlowNodesFromSweepSections,
    sweepSections,
    createFlowNodesFromSweepStickyNotesNodes,
    sweepStickyNotesNodes,
    sweepNodes,
    sweepGroups,
    createFlowNodesFromSweepNodes,
    sweepEdges,
    canvasMode,
    createFlowNodesFromSweepTextNodes,
    sweepTextNodes,
  ]);

  const mergeNodes = useCallback(() => {
    setCanvasNodes((_nodes, nodeOperations) => {
      const {
        getObject: getNode,
        addObject: addNode,
        setObject: setNode,
        getObjects: getNodes,
        removeObject: removeNode,
      } = nodeOperations;
      const { allNodes: newNodes } = calculateNodes();

      newNodes.forEach((node) => {
        const existingNode = getNode(node.id);
        if (existingNode) {
          setNode(node.id, {
            ...existingNode,
            ...node,
            data: { ...existingNode.data, ...node.data },
          } as any);
        } else {
          addNode(node);
        }
      });

      _nodes.forEach((node) => {
        if (!newNodes.some((n) => n.id === node.id)) {
          removeNode(node.id);
        }
      });
      // Removes regular nodes with no parents
      getNodes().forEach((node) => {
        if (node.type === CanvasElementType.REGULAR && !node.parentId) {
          removeNode(node.id);
        }
        if (node.parentId && !getNode(node.parentId)) {
          removeNode(node.id);
        }
      });

      return getNodes();
    });
  }, [calculateNodes, setCanvasNodes]);

  useEffect(() => {
    mergeNodes();
  }, [mergeNodes]);

  useEffect(() => {
    setCanvasEdges(calculateEdges());
    stateInitialized.current = true;
  }, [calculateEdges, setCanvasEdges, stateInitialized]);

  return { nodes: _nodes, edges: _edges };
};
